import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail } from 'lucide-react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

/**
 * Controlled dialog that renders the EXACT invite email a recipient receives.
 * Fetches the rendered HTML from the backend and shows it inside a sandboxed
 * iframe so the host sees a pixel-accurate preview (including QR codes & layout).
 */
export const EmailPreviewDialog = ({ event, message = '', open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !event) return undefined;
    let active = true;
    const t = setTimeout(async () => {
      setLoading(true);
      setData(null);
      try {
        const r = await api.post(`/events/${event.id}/preview-invite`, { custom_message: message });
        if (active) setData(r.data);
      } catch (e) {
        if (active) toast.error('Could not load email preview');
      } finally {
        if (active) setLoading(false);
      }
    }, 0);
    return () => { active = false; clearTimeout(t); };
  }, [open, event, message]);

  const subject = data?.subject || (event ? `You're invited: ${event.title}` : '');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0" data-testid="email-preview-dialog">
        <DialogHeader className="px-5 pt-5 pb-4 border-b border-border space-y-2 text-left">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" /> Invite email preview
          </DialogTitle>
          <div className="text-sm space-y-0.5">
            <div><span className="text-muted-foreground">From: </span><span className="text-foreground">Thrive Gatherings</span></div>
            <div className="truncate"><span className="text-muted-foreground">Subject: </span><span className="text-foreground font-medium" data-testid="email-preview-subject">{subject}</span></div>
          </div>
          <p className="text-xs text-muted-foreground">This is exactly what your recipients will see in their inbox.</p>
        </DialogHeader>
        <div className="bg-[#FBF7F0] h-[66vh] overflow-hidden">
          {loading || !data ? (
            <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <iframe
              title="Invite email preview"
              srcDoc={data.html}
              className="w-full h-full border-0 bg-white"
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              data-testid="email-preview-iframe"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
